// BookSlot.jsx — Redesigned: compact, responsive, content-dense
import { useState, useEffect, useCallback, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { getSlots, bookSlot } from "../services/api";
import "./BookSlot.css";
import { API } from "../services/api";
/* ───── SVG Icons ───── */
const I = {
  back: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 2 4 7l5 5" />
    </svg>
  ),
  arrow: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 7h10M8 3l4 4-4 4" />
    </svg>
  ),
  close: (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M2 2l8 8M10 2l-8 8" />
    </svg>
  ),
  check: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12.5 10 18.5 20 7" />
    </svg>
  ),
  clock: (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="7" r="5.5" />
      <path d="M7 4v3l2 1.5" />
    </svg>
  ),
  cal: (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="10" height="9" rx="1.5" />
      <path d="M5 1.5v3M9 1.5v3M2 6h10" />
    </svg>
  ),
  empty: (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.35 }}>
      <rect x="3" y="5" width="22" height="19" rx="2.5" />
      <path d="M3 11h22M9 3v4M19 3v4" />
    </svg>
  ),
};

/* ───── Helpers ───── */
const fmtDate = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const timeToMinutes = (timeStr) => {
  const [time, meridian] = timeStr.trim().split(" ");
  let [hours, minutes] = time.split(":").map(Number);

  if (meridian.toUpperCase() === "PM" && hours !== 12) hours += 12;
  if (meridian.toUpperCase() === "AM" && hours === 12) hours = 0;

  return hours * 60 + minutes;
};

const getDateLabel = (d) => {
  const today = new Date();
  const tom = new Date();
  tom.setDate(today.getDate() + 1);
  if (fmtDate(d) === fmtDate(today)) return "Today";
  if (fmtDate(d) === fmtDate(tom)) return "Tomorrow";
  return d.toLocaleDateString("en-IN", { weekday: "short" });
};

const getDates = () =>
  Array.from({ length: 14 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return d;
  });

const EMPTY_FORM = { name: "", phone: "", email: "" };

const FORM_FIELDS = [
  { name: "name", label: "Full name", type: "text", placeholder: "e.g. Priya Sharma" },
  { name: "phone", label: "Phone", type: "tel", placeholder: "10-digit number", maxLength: 10, inputMode: "numeric" },
  { name: "email", label: "Email", type: "email", placeholder: "you@example.com" },
];

const isSunday = (date) => date.getDay() === 0;

const generateDefaultSlots = (date) => {
  if (isSunday(date)) return [];

  const slots = [];
  const start = 10 * 60; // 10:00 AM
  const end = 17 * 60;   // 5:00 PM

  for (let mins = start; mins < end; mins += 15) {
    let h = Math.floor(mins / 60);
    const m = mins % 60;

    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;

    const time = `${h}:${String(m).padStart(2, "0")} ${ampm}`;

    slots.push({
      _id: `default-${fmtDate(date)}-${mins}`,
      time,
      isBooked: false,
    });
  }

  return slots;
};

/* ───── Component ───── */
export default function BookSlot() {
  const dates = getDates();
  const [selDate, setSelDate] = useState(dates[0]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selSlot, setSelSlot] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [submitErr, setSubmitErr] = useState("");

  const scrollRef = useRef(null);

  const loadSlots = useCallback(async (date) => {
    setLoading(true);
    setSelSlot(null);

    try {
      const allSlots = generateDefaultSlots(date);

      const res = await API.get(`/booked-slots?date=${fmtDate(date)}`);
      const data = res.data || {};
      const bookedTimes = data.booked_times || [];

      const now = new Date();

      const finalSlots = allSlots.map((slot) => {
        const [time, meridian] = slot.time.split(" ");
        let [hours, minutes] = time.split(":").map(Number);

        if (meridian === "PM" && hours !== 12) hours += 12;
        if (meridian === "AM" && hours === 12) hours = 0;

        const slotDateTime = new Date(date);
        slotDateTime.setHours(hours, minutes, 0, 0);

        const isPast = slotDateTime <= now;
        const isBooked = bookedTimes.includes(slot.time);

        return {
          ...slot,
          isBooked: isBooked || isPast, // 🔥 KEY LINE
        };
      });

      setSlots(finalSlots);
    } catch (err) {
      console.error(err);
      setSlots([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSlots(selDate); }, [selDate, loadSlots]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollLeft = 0;
  }, []);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!/^[6-9]\d{9}$/.test(form.phone)) e.phone = "Enter a valid 10-digit number";
    if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Enter a valid email";
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((err) => ({ ...err, [name]: "" }));
  };

  const handleBook = async (e) => {
    e.preventDefault();
    if (!selSlot) return;
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    setSubmitErr("");
    try {
      const res = await bookSlot({
        date: fmtDate(selDate),
        time: selSlot.time,
        name: form.name,
        phone: form.phone,
        email: form.email,
      });

      if (window.fbq) {
        window.fbq("track", "Lead", {
          content_name: "Counselling Slot Booking",
          status: "completed"
        });
      }

      setResult(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || "Booking failed. Please try again.";
      setSubmitErr(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setResult(null);
    setSelSlot(null);
    setForm(EMPTY_FORM);
    setSubmitErr("");
    setErrors({});
    loadSlots(selDate);
  };

  const available = slots.filter((s) => !s.isBooked);

  /* ─── Success Screen ─── */
  if (result) {
    return (
      <div className="book-root">
        <main className="book-main">
          <div className="book-success">
            <div className="book-success-badge">
              <span className="book-success-ring" />
              {I.check}
            </div>
            <h1 className="book-success-title">Booking confirmed</h1>
            <p className="book-success-sub">
              {result.message || "Your counselling slot is secured. We'll reach out before the session."}
            </p>
            <dl className="book-success-grid">
              <div className="book-success-row">
                <dt>Date</dt>
                <dd>{result.slot_date || fmtDate(selDate)}</dd>
              </div>
              <div className="book-success-row">
                <dt>Time</dt>
                <dd>{result.slot_time || selSlot?.time || "—"}</dd>
              </div>
              {result.booking_id && (
                <div className="book-success-row">
                  <dt>Reference</dt>
                  <dd className="book-mono">#{String(result.booking_id).slice(-8).toUpperCase()}</dd>
                </div>
              )}
            </dl>
            <button className="book-btn book-btn-primary" onClick={reset}>
              Book another slot {I.arrow}
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  /* ─── Main Screen ─── */
  return (
    <div className="book-root">
      <Helmet>
        <title>Book a Free Counselling Slot | ISTP Overseas Vijayawada</title>
        <meta name="description" content="Book a free study abroad counselling session with ISTP Overseas experts. Get personalised guidance on universities, IELTS, visas and scholarships." />
        <link rel="canonical" href="https://www.istpoverseas.com/book-slot" />
        <meta property="og:title" content="Book Free Counselling | ISTP Overseas" />
        <meta property="og:description" content="Schedule your free overseas education counselling session with ISTP Overseas today." />
        <meta property="og:url" content="https://www.istpoverseas.com/book-slot" />
      </Helmet>
      <main className="book-main">
        <header className="book-intro">
          <span className="book-eyebrow">{I.cal} Free counselling</span>
          <h1 className="book-title">
            Book a <em>counselling</em> slot
          </h1>
          <p className="book-sub">
            One-on-one guidance from our advisors. Pick a date and time that works for you.
          </p>
        </header>

        <div className="book-layout">
          {/* Left column: date + slots */}
          <section className="book-panel">
            <div className="book-panel-head">
              <h2 className="book-panel-title">Pick a date</h2>
              <span className="book-panel-hint">Next 14 days</span>
            </div>

            <div className="book-dates">
              <div className="book-dates-track" ref={scrollRef}>
                {dates.map((d) => {
                  const key = fmtDate(d);
                  const active = key === fmtDate(selDate);
                  return (
                    <button
                      key={key}
                      className={`book-date${active ? " is-active" : ""}`}
                      onClick={() => setSelDate(d)}
                      type="button"
                    >
                      <span className="book-date-label">{getDateLabel(d)}</span>
                      <span className="book-date-num">{d.getDate()}</span>
                      <span className="book-date-mon">
                        {d.toLocaleDateString("en-IN", { month: "short" })}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="book-panel-head" style={{ marginTop: 20 }}>
              <h2 className="book-panel-title">Available times</h2>
              {!loading && slots.length > 0 && (
                <span className="book-pill book-pill-green">
                  {available.length} open
                </span>
              )}
            </div>

            {loading ? (
              <div className="book-spinner" />
            ) : slots.length === 0 ? (
              <div className="book-empty">
                {I.empty}
                <p className="book-empty-title">No slots for this day</p>
                <p className="book-empty-sub">Try picking another date above</p>
              </div>
            ) : (
              <div className="book-slots">
                {slots.map((s) => {
                  const id = s._id || s.slot_id;
                  const selected = (selSlot?._id || selSlot?.slot_id) === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`book-slot${s.isBooked ? " is-booked" : ""}${selected ? " is-selected" : ""}`}
                      disabled={s.isBooked}
                      onClick={() => setSelSlot(s)}
                    >
                      <span className="book-slot-time">{s.time}</span>
                      {s.isBooked && <span className="book-slot-tag">Booked</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {/* Right column: form */}
          <aside className="book-panel book-form-panel">
            <div className="book-panel-head">
              <h2 className="book-panel-title">Your details</h2>
            </div>

            {selSlot ? (
              <div className="book-summary">
                <div className="book-summary-info">
                  <span className="book-summary-date">
                    {selDate.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
                  </span>
                  <span className="book-summary-time">{selSlot.time}</span>
                </div>
                <button
                  type="button"
                  className="book-summary-clear"
                  onClick={() => setSelSlot(null)}
                  aria-label="Clear selection"
                >
                  {I.close}
                </button>
              </div>
            ) : (
              <div className="book-summary book-summary-prompt">
                Select a time slot to continue
              </div>
            )}

            {submitErr && <div className="book-alert">{submitErr}</div>}

            <form onSubmit={handleBook} noValidate className="book-form">
              {FORM_FIELDS.map((f) => (
                <div className="book-field" key={f.name}>
                  <label htmlFor={f.name}>{f.label}</label>
                  <input
                    id={f.name}
                    name={f.name}
                    type={f.type}
                    placeholder={f.placeholder}
                    maxLength={f.maxLength}
                    inputMode={f.inputMode}
                    value={form[f.name]}
                    onChange={handleChange}
                    disabled={!selSlot}
                    autoComplete="off"
                  />
                  {errors[f.name] && <span className="book-field-err">{errors[f.name]}</span>}
                </div>
              ))}

              <button
                type="submit"
                className="book-btn book-btn-primary book-submit"
                disabled={!selSlot || submitting}
              >
                {submitting ? "Booking…" : <>Confirm booking {I.arrow}</>}
              </button>
            </form>

            <p className="book-note">
              {I.clock} Our advisor will confirm before the session
            </p>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}


function Footer() {
  return (
    <footer className="book-footer">
      <span>© 2026 ISTP Overseas Education.</span>
      <span>All rights reserved.</span>
    </footer>
  );
}